import { useCallback, useEffect } from 'react'

export default function useCompactQueueEventListeners({
  queueState,
  queueActions,
  contextMenu,
  setContextMenu,
  setShiftIsPressed,
  setLastCheckedIndex,
  filterRef,
}) {
  const checkedCount = Object.keys(queueState.selectedCheckboxes || {}).length
  const handleSpace = useCallback(
    (event, column) => {
      if (filterRef?.current?.contains(event.target)) {
        // If the event target is within the filterRef, do not propagate the space key event
        event.stopPropagation()
        return
      }
      event.preventDefault()

      const tableRows = document.querySelectorAll('#compactQueueTable tbody tr')
      const checkboxes = document.querySelectorAll('input[id*="messageCheckbox"]')
      const currentIndex = Number(Array.from(checkboxes).findIndex((checkbox) => checkbox.name === column))

      if (currentIndex + 1 <= checkboxes.length) {
        queueActions.addCheckboxSelection(queueState.queueContents[currentIndex], true)
        setLastCheckedIndex(currentIndex)
      }
      if (currentIndex + 1 < checkboxes.length) {
        checkboxes[currentIndex + 1].focus()
        tableRows[currentIndex + 1].style.backgroundColor = 'lightgray'
        tableRows[currentIndex].style.backgroundColor = ''
      }
    },
    [filterRef, queueActions, queueState.queueContents, setLastCheckedIndex]
  )
  const handleTab = useCallback(
    (event, column) => {
      event.preventDefault()

      const tableRows = document.querySelectorAll('#compactQueueTable tbody tr')
      const checkboxes = document.querySelectorAll('input[id*="messageCheckbox"]')
      const currentIndex = Number(Array.from(checkboxes).findIndex((checkbox) => checkbox.name === column))

      if (currentIndex + 1 <= checkboxes.length) {
        checkboxes[currentIndex + 1].focus()
        tableRows[currentIndex + 1].style.backgroundColor = 'lightgray'
        tableRows[currentIndex].style.backgroundColor = ''
      }
      setLastCheckedIndex(currentIndex)
    },
    [setLastCheckedIndex]
  )
  const handleContextMenu = useCallback(
    (event) => {
      event.preventDefault()

      setContextMenu({
        x: event.pageX,
        y: event.pageY - 150,
      })
    },
    [setContextMenu]
  )

  const closeContextMenu = useCallback(() => {
    setContextMenu(null)
  }, [setContextMenu])

  useEffect(() => {
    const myElement = document.getElementById('compactQueueTable')
    const contextMenuHandler = (event) => {
      // Exclude links and their children from triggering the context menu
      if (event.target.closest('a')) return
      handleContextMenu(event)
    }
    myElement?.addEventListener('contextmenu', contextMenuHandler)
    return () => {
      myElement?.removeEventListener('contextmenu', contextMenuHandler)
    }
  }, [handleContextMenu])

  useEffect(() => {
    if (contextMenu && (checkedCount <= queueState.checkboxConfirmLimit || !queueState.confirmDialogOpen)) {
      document.addEventListener('click', closeContextMenu)
    } else {
      document.removeEventListener('click', closeContextMenu)
    }

    return () => {
      document.removeEventListener('click', closeContextMenu)
    }
  }, [
    checkedCount,
    closeContextMenu,
    contextMenu,
    queueState.checkboxConfirmLimit,
    queueState.confirmDialogOpen,
    queueState.selectedCheckboxes,
  ])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Shift') {
        setShiftIsPressed(true)
      }

      if (event.key === ' ') {
        handleSpace(event, event.target.name)
      }

      if (event.key === 'Tab') {
        handleTab(event, event.target.name)
      }
    }

    const handleKeyUp = (event) => {
      if (event.key === 'Shift') {
        setShiftIsPressed(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [handleSpace, handleTab, setShiftIsPressed])

  useEffect(() => {
    const tableRows = document.querySelectorAll('#compactQueueTable tbody tr')

    const handleMouseEnter = (event) => {
      event.currentTarget.style.backgroundColor = 'lightgray'
    }

    const handleMouseLeave = (event) => {
      event.currentTarget.style.backgroundColor = ''
    }

    tableRows.forEach((row) => {
      row.addEventListener('mouseenter', handleMouseEnter)
      row.addEventListener('mouseleave', handleMouseLeave)
    })

    return () => {
      tableRows.forEach((row) => {
        row.removeEventListener('mouseenter', handleMouseEnter)
        row.removeEventListener('mouseleave', handleMouseLeave)
      })
    }
  }, [queueState.queueContents])
}
